import type { ReadingCopy } from "./types";
import { isJsonObject } from "./json";

const METHODS = new Set(["model", "feed", "readability", "email", "saved_source"]);

export function validReadingCopy(value: unknown): value is ReadingCopy {
  return isJsonObject(value)
    && value.version === "imprint.reading-copy.v1"
    && ["title", "publication", "compatibility_prefix", "completeness"].every((key) => typeof value[key] === "string")
    && ["subtitle", "published_date", "publisher_url", "outbound_url"].every((key) => value[key] === null || typeof value[key] === "string")
    && Array.isArray(value.authors) && value.authors.every((author) => typeof author === "string")
    && typeof value.method === "string" && METHODS.has(value.method)
    && Array.isArray(value.figures) && value.figures.every((figure) => isJsonObject(figure)
      && ["url", "path", "alt", "caption", "credit"].every((key) => typeof figure[key] === "string"));
}

export function readingCopyBody(markdown: string, copy?: ReadingCopy | null): string {
  const prefix = copy?.compatibility_prefix;
  // Older clients read this prefix as Markdown. Remove only the exact server
  // representation, never a matching phrase elsewhere in the author's text.
  return prefix && markdown.startsWith(prefix) ? markdown.slice(prefix.length) : markdown;
}

export function sourceActionUrl(value?: string | null): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol)
      && !url.username && !url.password
      && !["redacted.invalid", "imprint.invalid"].includes(url.hostname)
      ? url.href : undefined;
  } catch { return undefined; }
}

export function publicationDate(value?: string | null): string {
  if (!value) return "";
  // A source's calendar date should not move to yesterday in a US time zone.
  const match = value.match(/^(\d{4}-\d{2}-\d{2})(?:$|T)/);
  if (!match) return "";
  const date = new Date(`${match[1]}T00:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== match[1]) return "";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(date);
}
